import { deriveSlug } from '../fmBundles';

/** One selected model row in the Models step. */
export interface SelectedModel {
  modelId: string;
  modelName: string;
  provider: string;
  shortName: string;
}

export type BudgetPeriod = 'monthly' | 'yearly';

/** Full wizard form state. */
export interface FmWizardState {
  projectId: string;
  projectName: string;
  environmentId: string; // '' | '__new__' | documentId
  newEnvName: string;
  newEnvIsProduction: boolean;
  team: string;
  serviceName: string;
  serviceNameTouched: boolean;
  models: SelectedModel[];
  computeRoles: string[];
  budgetPeriod: BudgetPeriod;
  budgetAmount: string;
  businessEmails: string[];
}

export const NEW_ENV_VALUE = '__new__';

/**
 * AWS-derived name-length limits. These mirror the API constants in
 * server/src/services/fm-bundle-constants.ts — keep both in sync.
 *   SERVICE_NAME_MAX (30): hard cap on the service name.
 *   SHORT_NAME_MAX (20): hard cap on each model short name.
 *   ROLE_NAME_BUDGET (40): serviceName + envName must fit, because the IAM role name is
 *     "<svc>-foundation-models-role-<env>" (24 literal chars) against IAM's 64-char limit.
 *   AIP_NAME_BUDGET (62): serviceName + shortName + envName must fit, because an inference
 *     profile name is "<svc>-<shortName>-<env>" (2 hyphens) against the 64-char limit.
 */
export const SERVICE_NAME_MAX = 30;
export const SHORT_NAME_MAX = 20;
export const ROLE_NAME_BUDGET = 40;
export const AIP_NAME_BUDGET = 62;

export const INITIAL_STATE: FmWizardState = {
  projectId: '',
  projectName: '',
  environmentId: '',
  newEnvName: '',
  newEnvIsProduction: false,
  team: '',
  serviceName: '',
  serviceNameTouched: false,
  models: [],
  computeRoles: [''],
  budgetPeriod: 'monthly',
  budgetAmount: '',
  businessEmails: [],
};

export const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;
export const ARN_RE = /^arn:aws:iam::\d{12}:role\/[\w+=,.@/-]+$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Per-field errors keyed by a dotted field path (e.g. "models.0.shortName"). */
export type Errors = Record<string, string>;

/** Builds a unique short name from a model name, deduplicating against `existing` with -2, -3. */
export function uniqueShortName(modelName: string, existing: string[]): string {
  const base = deriveSlug(modelName) || 'model';
  if (!existing.includes(base)) return base;
  let n = 2;
  while (existing.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}
